import React, { useState, useRef } from 'react';
import { Button } from '../core/button';
import { Progress } from '../core/progress';
import { Card, CardContent, CardHeader, CardTitle } from '../core/card';
import { Alert, AlertTitle, AlertDescription } from '../core/alert';
import { Upload, X, FileIcon, CheckCircle, AlertCircle } from 'lucide-react';
import { useMediaUpload } from '../hooks/use-media-upload';
import { useTranslation } from 'react-i18next';

export interface MediaUploaderProps {
  onUploadComplete: (url: string, mediaId?: string) => void;
  onError?: (error: Error) => void;
  acceptedFileTypes?: string[];
  maxFileSize?: number; // in bytes
  title?: string;
  description?: string;
  compact?: boolean;
}

export const MediaUploader: React.FC<MediaUploaderProps> = ({
  onUploadComplete,
  onError,
  acceptedFileTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'video/mp4',
    'video/webm',
    'audio/mp3',
    'audio/mpeg',
    'audio/wav',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ],
  maxFileSize = 10 * 1024 * 1024 * 1024, // 10GB default (support large videos)
  title,
  description,
  compact = false
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadState, uploadMedia, resetUpload } = useMediaUpload();
  const { t } = useTranslation('admin');
  const resolvedTitle = title ?? t('mediaUploader.defaultTitle');
  const resolvedDescription = description ?? t('mediaUploader.defaultDescription');

  const resetState = () => {
    setSelectedFile(null);
    resetUpload();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const validateFile = (file: File): boolean => {
    if (!acceptedFileTypes.includes(file.type)) {
      const error = new Error(`File type not supported. Accepted types: ${acceptedFileTypes.join(', ')}`);
      console.error('File validation failed:', error.message);
      if (onError) onError(error);
      return false;
    }
    if (file.size > maxFileSize) {
      const error = new Error(`File too large. Maximum size: ${Math.round(maxFileSize / (1024 * 1024))}MB`);
      console.error('File validation failed:', error.message);
      if (onError) onError(error);
      return false;
    }
    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
        // Auto-upload the file immediately
        uploadMedia(file).then(result => {
          if (result && result.mediaUrl) {
            onUploadComplete(result.mediaUrl, result.mediaId);
          }
        }).catch(err => {
          if (onError) onError(err as Error);
        });
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
        // Auto-upload the file immediately
        uploadMedia(file).then(result => {
          if (result && result.mediaUrl) {
            onUploadComplete(result.mediaUrl, result.mediaId);
          }
        }).catch(err => {
          if (onError) onError(err as Error);
        });
      }
    }
  };

  // Helper to render the current upload state
  const renderUploadState = () => {
    switch (uploadState.status) {
      case 'requesting-urls':
      case 'uploading':
      case 'composing':
        const showChunkInfo = uploadState.currentChunk && uploadState.totalChunks;
        return (
          <div className={compact ? "space-y-2" : "space-y-4"}>
            <Progress value={uploadState.progress} className="w-full" />
            <p className={compact ? "text-sm text-gray-500" : "text-center text-sm text-gray-500"}>
              {uploadState.status === 'requesting-urls' && t('mediaUploader.preparing')}
              {uploadState.status === 'uploading' &&
                showChunkInfo &&
                t('mediaUploader.chunkProgress', {
                  current: uploadState.currentChunk,
                  total: uploadState.totalChunks,
                  percent: uploadState.progress ?? 0,
                })}
              {uploadState.status === 'uploading' &&
                !showChunkInfo &&
                t('mediaUploader.progress', { percent: uploadState.progress ?? 0 })}
              {uploadState.status === 'composing' && t('mediaUploader.composing')}
            </p>
          </div>
        );
      case 'success':
        if (compact) {
          return (
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-sm">{t('common.status.uploadComplete')}</span>
              <Button size="sm" variant="outline" onClick={resetState}>
                <X className="h-4 w-4 mr-1" /> {t('common.actions.clear')}
              </Button>
            </div>
          );
        }
        return (
          <div className="flex flex-col items-center space-y-4">
            <CheckCircle className="h-12 w-12 text-green-500" />
            <p>{t('common.status.uploadComplete')}</p>
            <Button onClick={resetState}>{t('mediaUploader.uploadAnother')}</Button>
          </div>
        );
      case 'idle':
      case 'error':
      default:
        if (selectedFile) {
          return compact ? (
            <div className="flex flex-col space-y-2">
              <div className="flex items-center space-x-2">
                <FileIcon className="h-5 w-5" />
                <span className="text-sm font-medium truncate">{selectedFile.name}</span>
                <Button size="sm" variant="ghost" onClick={resetState}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex space-x-2">
                <Button size="sm" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="h-4 w-4 mr-1" /> {t('common.actions.upload')}
                </Button>
                <Button size="sm" variant="outline" onClick={resetState}>
                  {t('common.actions.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 p-2 border rounded-md">
                <FileIcon className="h-8 w-8" />
                <div className="flex-1">
                  <p className="font-medium truncate">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">{Math.round(selectedFile.size / 1024)} KB</p>
                </div>
                <Button size="sm" variant="ghost" onClick={resetState}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex space-x-2">
                <Button onClick={() => fileInputRef.current?.click()} className="flex-1">
                  <Upload className="h-4 w-4 mr-1" /> {t('common.actions.upload')}
                </Button>
                <Button variant="outline" onClick={resetState}>
                  {t('common.actions.cancel')}
                </Button>
              </div>
            </div>
          );
        }
        
        // Default state - file selection
        return compact ? (
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-4 w-4 mr-1" /> {t('common.actions.selectFile')}
          </Button>
        ) : (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center ${
              dragActive ? 'border-primary bg-muted/50' : 'border-border'
            }`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
            <p className="mt-2 font-medium">{resolvedDescription}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('mediaUploader.supports')}: {acceptedFileTypes.map((type) => type.split('/')[1]).join(', ')}
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => fileInputRef.current?.click()}
            >
              {t('common.actions.selectFile')}
            </Button>
          </div>
        );
    }
  };

  if (compact) {
    return (
      <div className="w-full">
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          accept={acceptedFileTypes.join(',')}
          className="hidden"
        />

        {uploadState.error && (
          <Alert variant="destructive" className="mb-2">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{uploadState.error}</AlertDescription>
          </Alert>
        )}

        {renderUploadState()}
      </div>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
            <CardTitle>{resolvedTitle}</CardTitle>
      </CardHeader>
      <CardContent>
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          accept={acceptedFileTypes.join(',')}
          className="hidden"
        />

        {uploadState.error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{uploadState.error}</AlertDescription>
          </Alert>
        )}

        {renderUploadState()}
      </CardContent>
    </Card>
  );
};